import Box from '@mui/material/Box';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormState } from 'react-hook-form';

import { Button } from 'common/components/Button';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { ConfirmDialog } from 'common/components/ConfirmDialog';

interface IProps {
    canDelete?: boolean;
    children?: React.ReactNode;
    deleteDialogText?: string;
    deleteDialogTitle?: string;
    disabled?: boolean;
    submitButtonText?: string;
    onClose?: () => void;
    onDelete?: () => void | Promise<void>;
}

export const FormFooter = ({
    canDelete,
    children,
    deleteDialogText,
    deleteDialogTitle,
    disabled,
    submitButtonText,
    onClose,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const { isSubmitting, isDirty } = useFormState();

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!onDelete) return;

        setIsDeleting(true);

        try {
            await onDelete();
        } finally {
            setIsDeleting(false);
            setDeleteDialogOpen(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    mt: 3,
                    alignItems: 'center',
                }}
                {...other}
            >
                <Button
                    color="primary"
                    disabled={isSubmitting || disabled || !isDirty}
                    loading={isSubmitting}
                    type="submit"
                    variant="contained"
                >
                    {submitButtonText || translate('save')}
                </Button>

                {onClose && (
                    <Button
                        disabled={isSubmitting}
                        sx={{ ml: 2 }}
                        variant="contained"
                        onClick={onClose}
                    >
                        {translate('cancel')}
                    </Button>
                )}

                {canDelete && (
                    <Box sx={{ ml: 'auto' }}>
                        <FormDeleteButton
                            disabled={isSubmitting}
                            onClick={handleDeleteClick}
                        >
                            {translate('delete')}
                        </FormDeleteButton>
                    </Box>
                )}
            </Box>

            {canDelete && (
                <ConfirmDialog
                    color="error"
                    confirmText={translate('delete')}
                    loading={isDeleting}
                    open={deleteDialogOpen}
                    title={deleteDialogTitle || ''}
                    onCancel={handleDeleteCancel}
                    onClose={handleDeleteCancel}
                    onConfirm={handleDeleteConfirm}
                >
                    {deleteDialogText || ''}
                </ConfirmDialog>
            )}
        </>
    );
};
